import React from "react";
import { Box, ButtonBase, CircularProgress, IconButton, InputBase } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { COR, FONTE } from "../LabUi";
import avatarEstatico from "./oxebot-avatar.png";

// Peças visuais do widget OxeBot (mockup de 07/10/2026: Poppins, tons creme/laranja).
// Só apresentação — nenhuma regra do bot/chamados mora aqui.

export { COR, FONTE };

// Tokens do widget: reaproveita o kit LabUi e acrescenta os tons que só existem aqui.
export const OXB = {
  laranja: COR.primaria, // #C4561F
  laranjaHover: COR.primariaHover, // #A8450F
  laranjaFim: "#D97A3A", // fim do degradê do cabeçalho
  pessego: COR.primariaClara, // #FBEDE4
  pessegoBorda: "#F3D2BF",
  realce: "#FFF6EF", // hover/realce das sugestões
  fundo: "#FDF8F1", // fundo creme da janela
  bege: "#F3EDE3", // input, chip "Hoje", pílula "Meus chamados"
  begeBorda: "#E7DDCE",
  desabilitado: "#EAE3D8",
  desabilitadoTexto: "#B5AC9F",
  borda: COR.borda, // #EDE4D6
  linha: COR.linha, // #F1EBE1
  tinta: COR.tinta, // #1F1E1C
  suave: COR.suave, // #6B655D
  apagado: COR.apagado, // #8C8478
  verde: COR.verde,
  verdeClaro: COR.verdeClaro,
  verdeBorda: "#CFE6D9",
  vermelho: COR.vermelho,
  vermelhoClaro: COR.vermelhoClaro,
  azul: COR.azul,
  azulClaro: COR.azulClaro,
  online: "#2E9B5F", // texto "Online agora" no balão
  pontoOnline: "#34C26F",
  pontoOnlineClaro: "#8EF0B5", // bolinha sobre o degradê laranja
  escuro: "#26221E", // fundo do avatar do robô
};

// Robô animado num círculo escuro. O vídeo (com alpha) é o mesmo que o botão
// flutuante já usava em public/assets.
export function AvatarBot({ tamanho = 40, borda = false }) {
  return (
    <Box
      sx={{
        width: tamanho, height: tamanho, borderRadius: "50%", flexShrink: 0,
        bgcolor: OXB.escuro, overflow: "hidden", boxSizing: "border-box",
        border: borda ? "2px solid #fff" : "none",
        boxShadow: borda ? "0 4px 12px rgba(60,20,0,.25)" : "none",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <video
        autoPlay loop muted playsInline
        src="/assets/oxebot-icon.webm"
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
    </Box>
  );
}

// Versão estática (último quadro do mesmo vídeo) para os avatares pequenos sob as
// mensagens — evita um <video> decodificando por mensagem.
export function AvatarBotMini({ tamanho = 22 }) {
  return (
    <Box
      component="img"
      src={avatarEstatico}
      alt=""
      aria-hidden="true"
      sx={{ width: tamanho, height: tamanho, borderRadius: "50%", bgcolor: OXB.escuro, flexShrink: 0, display: "block" }}
    />
  );
}

export function PontoOnline({ cor = OXB.pontoOnline, tamanho = 8, halo }) {
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        width: tamanho, height: tamanho, borderRadius: "50%", bgcolor: cor,
        display: "inline-block", flexShrink: 0,
        boxShadow: halo ? `0 0 0 3px ${halo}` : "none",
      }}
    />
  );
}

// Botão quadrado translúcido do cabeçalho ("–" minimizar e "×" fechar).
export function BotaoTopo({ children, rotulo, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={rotulo}
      title={rotulo}
      sx={{
        width: 32, height: 32, borderRadius: "10px", flexShrink: 0,
        bgcolor: "rgba(255,255,255,.18)", color: "#fff",
        transition: "background-color .15s",
        "&:hover": { bgcolor: "rgba(255,255,255,.3)" },
        "&.Mui-focusVisible": { outline: "2px solid #fff", outlineOffset: "2px" },
      }}
    >
      {children}
    </ButtonBase>
  );
}

// Link "← Voltar ao chat" das telas internas.
export function LinkVoltar({ children, onClick, discreto = false }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        fontFamily: FONTE, fontSize: 13, fontWeight: 600,
        color: discreto ? OXB.apagado : OXB.laranja,
        borderRadius: "8px", px: "6px", py: "4px", ml: discreto ? 0 : "-6px",
        transition: "background-color .15s",
        "&:hover": { bgcolor: discreto ? OXB.bege : OXB.pessego },
      }}
    >
      {children}
    </ButtonBase>
  );
}

export function TituloTela({ children }) {
  return (
    <Box
      component="h2"
      sx={{ fontFamily: FONTE, fontSize: 19, fontWeight: 700, color: OXB.tinta, letterSpacing: "-.01em", lineHeight: 1.3, m: 0, mt: "8px" }}
    >
      {children}
    </Box>
  );
}

// Pílula de ação do rodapé: "laranja" (Abrir chamado) ou "bege" (Meus chamados).
export function PilulaAcao({ children, tom = "laranja", onClick, title }) {
  const laranja = tom === "laranja";
  return (
    <ButtonBase
      onClick={onClick}
      title={title}
      sx={{
        fontFamily: FONTE, fontSize: 12.5, fontWeight: 600, lineHeight: 1.2,
        borderRadius: 99, px: "12px", py: "7px", gap: "6px",
        bgcolor: laranja ? OXB.pessego : OXB.bege,
        color: laranja ? OXB.laranja : OXB.tinta,
        border: `1px solid ${laranja ? OXB.pessegoBorda : OXB.begeBorda}`,
        transition: "border-color .15s, background-color .15s",
        "&:hover": { borderColor: laranja ? OXB.laranja : OXB.apagado },
      }}
    >
      {children}
    </ButtonBase>
  );
}

// Pílula de status do chamado (cores vêm de STATUS_LABELS).
export function PilulaStatus({ st, tamanho = 11.5 }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center", flexShrink: 0,
        px: "9px", py: "3px", borderRadius: 99,
        bgcolor: st?.bg, color: st?.color,
        fontFamily: FONTE, fontSize: tamanho, fontWeight: 700, lineHeight: 1.3, whiteSpace: "nowrap",
      }}
    >
      {st?.label}
    </Box>
  );
}

// Campo branco do formulário de chamado: raio 14, borda creme, ícone à esquerda.
export function CampoChamado({ icone, multiline = false, sx, ...props }) {
  return (
    <Box
      sx={{
        display: "flex", alignItems: multiline ? "flex-start" : "center", gap: "10px",
        bgcolor: "#fff", border: `1px solid ${OXB.borda}`, borderRadius: "14px",
        px: "14px", py: multiline ? "11px" : "10px",
        transition: "border-color .15s, box-shadow .15s",
        "&:focus-within": { borderColor: OXB.laranja, boxShadow: `0 0 0 3px ${OXB.pessego}` },
        ...sx,
      }}
    >
      {icone && (
        <Box sx={{ color: OXB.apagado, display: "flex", flexShrink: 0, "& svg": { fontSize: 19 } }}>
          {icone}
        </Box>
      )}
      <InputBase
        fullWidth
        multiline={multiline}
        {...props}
        sx={{
          fontFamily: FONTE, fontSize: 14, color: OXB.tinta, p: 0,
          "& .MuiInputBase-input": { p: 0, lineHeight: 1.5 },
          "& .MuiInputBase-input::placeholder": { color: OXB.apagado, opacity: 1 },
        }}
      />
    </Box>
  );
}

// Caixa de texto bege arredondada do rodapé (chat e resposta de chamado).
export function CaixaEntrada({ inputRef, ...props }) {
  return (
    <Box
      sx={{
        flex: 1, minWidth: 0, display: "flex", alignItems: "center",
        bgcolor: OXB.bege, border: "1px solid transparent", borderRadius: "22px",
        px: "16px", py: "10px",
        transition: "background-color .15s, border-color .15s",
        "&:focus-within": { bgcolor: "#fff", borderColor: OXB.pessegoBorda },
      }}
    >
      <InputBase
        inputRef={inputRef}
        fullWidth
        multiline
        maxRows={3}
        {...props}
        sx={{
          fontFamily: FONTE, fontSize: 14, color: OXB.tinta, p: 0,
          "& .MuiInputBase-input": { p: 0, lineHeight: 1.45 },
          "& .MuiInputBase-input::placeholder": { color: OXB.apagado, opacity: 1 },
        }}
      />
    </Box>
  );
}

// Botão circular de enviar (avião de papel).
export function BotaoEnviar({ ativo, carregando = false, onClick, rotulo = "Enviar" }) {
  return (
    <IconButton
      onClick={onClick}
      disabled={!ativo}
      aria-label={rotulo}
      title={rotulo}
      sx={{
        width: 42, height: 42, flexShrink: 0,
        bgcolor: OXB.laranja, color: "#fff",
        boxShadow: "0 6px 14px rgba(196,86,31,.28)",
        transition: "background-color .15s, box-shadow .15s",
        "&:hover": { bgcolor: OXB.laranjaHover },
        "&.Mui-disabled": { bgcolor: OXB.desabilitado, color: OXB.desabilitadoTexto, boxShadow: "none" },
      }}
    >
      {carregando
        ? <CircularProgress size={16} sx={{ color: OXB.laranja }} />
        : <SendIcon sx={{ fontSize: 18, ml: "2px" }} />}
    </IconButton>
  );
}
